import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import i18n from '../../../i18n';
import { getSiteSettings } from '../API/settings';
import { getSiteDisplayName } from '../utils/siteDisplayName';

// Cache keys
const CACHE_KEY = 'site_settings_cache';
const CACHE_TIMESTAMP_KEY = 'site_settings_cache_timestamp';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// Helper function to get cached settings
const getCachedSettings = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    
    if (cached && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age < CACHE_DURATION) {
        return JSON.parse(cached);
      }
    }
    return null;
  } catch (error) {
    console.error('Error reading cached settings:', error);
    return null;
  }
};

// Helper function to cache settings
const cacheSettings = (settings) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(settings));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('Error caching settings:', error);
  }
};

// Helper function to update document title and favicon
const updateDocumentElements = (data) => {
  document.title = getSiteDisplayName(data, i18n.language);
  
  // Only update favicon if site_icon is provided in settings
  if (data.site_icon && data.site_icon.trim() !== '') {
    // Remove any existing favicon links first
    const existingFavicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
    existingFavicons.forEach(link => link.remove());
    
    // Create new favicon link
    const favicon = document.createElement('link');
    favicon.rel = 'icon';
    favicon.href = data.site_icon;
    document.head.appendChild(favicon);
    
    // Update apple-touch-icon as well
    const existingAppleIcons = document.querySelectorAll('link[rel="apple-touch-icon"]');
    existingAppleIcons.forEach(link => link.remove());
    
    const appleIcon = document.createElement('link');
    appleIcon.rel = 'apple-touch-icon';
    appleIcon.href = data.site_icon;
    document.head.appendChild(appleIcon);
  } else {
    // If no site_icon is provided, remove any existing favicons to keep it blank
    const existingFavicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]');
    existingFavicons.forEach(link => link.remove());
  }
};

// Create context
const SiteSettingsContext = createContext();

// Provider component
export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSettings = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      
      // If not forcing refresh, try to get cached settings first
      if (!forceRefresh) {
        const cachedSettings = getCachedSettings();
        if (cachedSettings) {
          console.log('Site settings loaded from cache:', cachedSettings);
          setSettings(cachedSettings);
          updateDocumentElements(cachedSettings);
          setError(null);
          setLoading(false);
          return;
        }
      }
      
      // If forcing refresh or no cache, fetch from API
      console.log('SiteSettingsContext: Fetching site settings from API...');
      const data = await getSiteSettings();
      console.log('SiteSettingsContext: Site settings loaded from API:', data);
      
      // Cache the new settings
      cacheSettings(data);
      
      setSettings(data);
      updateDocumentElements(data);
      setError(null);
    } catch (err) {
      setError(err);
      console.error('Error in SiteSettingsProvider:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Function to refresh settings from API
  const refreshSettings = useCallback(() => {
    fetchSettings(true);
  }, [fetchSettings]);

  useEffect(() => {
    fetchSettings();
    
    // Listen for settings updates from admin panels
    const handleSettingsUpdate = () => {
      console.log('Settings update event received, refreshing...');
      fetchSettings(true);
    };
    
    if (typeof window !== 'undefined') {
      window.addEventListener('siteSettingsUpdated', handleSettingsUpdate);
      
      // Cleanup listener on unmount
      return () => {
        window.removeEventListener('siteSettingsUpdated', handleSettingsUpdate);
      };
    }
  }, [fetchSettings]);

  // Keep document.title in sync with UI language (ar/zh/en site names)
  useEffect(() => {
    if (!settings) return undefined;
    const syncTitle = () => {
      document.title = getSiteDisplayName(settings, i18n.language);
    };
    syncTitle();
    i18n.on('languageChanged', syncTitle);
    return () => {
      i18n.off('languageChanged', syncTitle);
    };
  }, [settings]);

  const value = useMemo(() => ({
    settings,
    loading,
    error,
    refreshSettings
  }), [settings, loading, error, refreshSettings]);

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

// Custom hook to use the context
export const useSiteSettings = () => {
  const context = useContext(SiteSettingsContext);
  if (context === undefined) {
    throw new Error('useSiteSettings must be used within a SiteSettingsProvider');
  }
  return context;
};
